import * as React from 'react'
import { useIntl } from 'react-intl'
import List from 'components/Common/List'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import MarkdownRender from 'LuxKit/MarkdownRender'
import Heading from 'LuxKit/Typography/Heading'
import ExperienceDetailsAboutLocationMap from './ExperienceDetailsAboutLocationMap'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Accordion from 'LuxKit/Accordion'
import useThingsToKnow from 'hooks/Experiences/useThingsToKnow'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'

interface Props {
  experienceView: App.ExperienceBookingView
  pickupPoint?: App.ExperiencePickupPoint
  showLocation?: boolean
  /**
   * The variant determines the order of the information displayed
   * @default inclusions-first
   */
  variant?: 'inclusions-first' | 'overview-first'
  /**
   * Provide your own title, otherwise will go with what's most relevant to the variant
   */
  title?: string
  /**
   * Optional notice to display inside the Location accordion, above the map
   */
  locationNotice?: string
  /**
   * When set, truncates the description to this height (px) with a "Read more" toggle.
   * Only applies when variant="overview-first".
   */
  descriptionMaxHeight?: number
  /**
   * Optional content to render between the description and the accordions.
   */
  afterDescription?: React.ReactNode
  /**
   * When provided, thingsToKnow items whose title matches this pattern (case-insensitive)
   * are excluded from their own accordion. Their content can be surfaced elsewhere
   * (e.g. inside the Location accordion via `locationNotice`).
   */
  excludeThingsToKnowPattern?: RegExp
}

function ExperienceDetailsModalAboutSection(props: Props) {
  const {
    variant = 'inclusions-first',
    experienceView,
    pickupPoint,
    showLocation = true,
    title,
    locationNotice,
    descriptionMaxHeight,
    afterDescription,
    excludeThingsToKnowPattern,
  } = props

  const intl = useIntl()

  const inclusionsLabel = intl.formatMessage({
    id: 'SFa7utIAzYUdZ2Zj6YpX',
    defaultMessage: 'Inclusions',
    description: 'Section heading for inclusions in experience details modal about section',
  })

  const overviewLabel = intl.formatMessage({
    id: 'RT5jlxGEABGs-3kzpgkq',
    defaultMessage: 'Overview',
    description: 'Section heading for overview in experience details modal about section',
  })

  const locationLabel = intl.formatMessage({
    id: 'MmZ7BhkWDfX5X0coMz7L',
    defaultMessage: 'Location',
    description: 'Accordion heading for location in experience details modal about section',
  })

  const readMoreText = intl.formatMessage({
    id: 'jKJxqc6aV81QjCbVcOrL',
    defaultMessage: 'Read more',
    description: 'Toggle to expand truncated description in experience details modal',
  })

  const readLessText = intl.formatMessage({
    id: 'gOeECIzGzAZ3lcWp6rZY',
    defaultMessage: 'Read less',
    description: 'Toggle to collapse expanded description in experience details modal',
  })

  const copy = experienceView.originalExperience.copy
  const { items: thingsToKnowItems, isLoading: thingsToKnowLoading } = useThingsToKnow(
    experienceView.originalExperience.id,
    copy.thingsToKnow,
  )
  const filteredThingsToKnow = thingsToKnowItems.filter(item => !excludeThingsToKnowPattern?.test(item.title))
  const descriptionContent = descriptionMaxHeight
    ? <LargeContentToggle maxHeight={descriptionMaxHeight} showMoreText={readMoreText} showLessText={readLessText} showChevron>
        <MarkdownRender content={copy.dealDescription ?? ''} />
    </LargeContentToggle>
    : <MarkdownRender content={copy.dealDescription ?? ''} />

  return (
    <VerticalSpacer gap={24}>
      {experienceView.type === 'channel_manager' && copy.highlights.length > 0 && (
        <VerticalSpacer gap={12}>
          <Heading variant="heading6">{intl.formatMessage({
            id: 'd3Ip5ZRTKjR3bdLRXamc',
            defaultMessage: 'Why you should do it!',
            description: 'Heading for highlights section in experience details modal',
          })}
          </Heading>
          <List>
            {copy.highlights.map((highlight, idx) => <li key={idx}>{highlight}</li>)}
          </List>
        </VerticalSpacer>
      )}
      {variant === 'inclusions-first' && copy.included && <VerticalSpacer gap={20}>
        <Heading variant="heading4">{title ?? inclusionsLabel}</Heading>
        <MarkdownRender type="compact" content={copy.included} />
      </VerticalSpacer>}
      {variant === 'overview-first' && copy.dealDescription && <VerticalSpacer gap={20}>
        <Heading variant="heading4">{title ?? overviewLabel}</Heading>
        {descriptionContent}
      </VerticalSpacer>}
      {afterDescription}
      <div>
        {variant !== 'overview-first' && copy.dealDescription && <Accordion heading={overviewLabel} hideDivider>
          <MarkdownRender content={copy.dealDescription ?? ''} />
        </Accordion>}
        {variant !== 'inclusions-first' && copy.included && <Accordion heading={inclusionsLabel} hideDivider>
          <MarkdownRender type="compact" content={copy.included} />
        </Accordion>}
        {showLocation && <Accordion heading={locationLabel} hideDivider>
          <VerticalSpacer gap={16}>
            {locationNotice && <MarkdownRender content={locationNotice} type="compact" />}
            {experienceView?.location &&
              <Heading as="h3" variant="heading3" align="center">
                {experienceView.location}
              </Heading>}
            <ExperienceDetailsAboutLocationMap
              experienceView={experienceView}
              pickupPoint={pickupPoint}
            />
          </VerticalSpacer>
        </Accordion>}
        {thingsToKnowLoading && <LoadingIndicator />}
        {!thingsToKnowLoading && filteredThingsToKnow.map((item, idx) => (
          <Accordion heading={item.title} key={`${item.title}-${idx}`} hideDivider>
            <MarkdownRender content={item.content} type="compact" />
          </Accordion>
        ))}
      </div>
    </VerticalSpacer>
  )
}

export default ExperienceDetailsModalAboutSection
